import type { TFunction } from "i18next";

export type ValuePropSlide = {
  headline: string;
  description: string;
};

/** i18n key for the `slides` array; also used as prefix for per-index `t('...0.headline')` resolution. */
const VALUE_PROP_SLIDES_KEY = "auth.registration.valueProp.slides";

function isValuePropSlide(x: unknown): x is ValuePropSlide {
  if (typeof x !== "object" || x === null) {
    return false;
  }
  const rec = x as Record<string, unknown>;
  return typeof rec.headline === "string" && typeof rec.description === "string";
}

/**
 * Resolves `auth.registration.valueProp.slides` from i18n. Handles a real JSON
 * array and the edge case where merged resources expose numeric-key objects.
 */
export function parseValuePropSlides(raw: unknown): ValuePropSlide[] {
  if (Array.isArray(raw)) {
    return raw.filter(isValuePropSlide);
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => /^\d+$/.test(k));
    if (keys.length > 0) {
      const sortedKeys = [...keys];
      sortedKeys.sort((a, b) => Number(a) - Number(b));
      return sortedKeys.map((k) => o[k]).filter(isValuePropSlide);
    }
  }
  return [];
}

type I18nApi = {
  language: string;
  resolvedLanguage?: string;
  getResourceBundle?: (lng: string, ns: string) => unknown;
};

/**
 * Read slides directly from the loaded i18n resource tree.
 * This avoids `returnObjects` and deep-merge quirks where every array index can share
 * one object (so headline/description look “stuck” when slideIndex changes).
 */
function getValuePropSlidesFromResource(i18n: I18nApi): ValuePropSlide[] {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const bundle = i18n.getResourceBundle?.(lng, "translation") as
    | Record<string, unknown>
    | null
    | undefined;
  if (!bundle) return [];
  const auth = bundle["auth"] as Record<string, unknown> | undefined;
  const registration = auth?.["registration"] as Record<string, unknown> | undefined;
  const valueProp = registration?.["valueProp"] as Record<string, unknown> | undefined;
  const raw = valueProp?.["slides"];
  return parseValuePropSlides(raw);
}

/**
 * Preferred resolver: bundle walk first (correct distinct slides), then per-key `t()`.
 */
export function resolveValuePropSlides(i18n: I18nApi, t: TFunction): ValuePropSlide[] {
  if (typeof i18n.getResourceBundle === "function") {
    const fromResource = getValuePropSlidesFromResource(i18n);
    if (fromResource.length > 0) {
      return fromResource;
    }
  }
  return buildValuePropSlidesFromT(t);
}

/**
 * Fallback when `getResourceBundle` is empty or unavailable: uses `returnObjects` for length,
 * then per-index `t('...slides.N.headline')` so each string resolves independently of shared array refs.
 */
function buildValuePropSlidesFromT(t: TFunction): ValuePropSlide[] {
  const raw = t(VALUE_PROP_SLIDES_KEY, { returnObjects: true });
  const shape = parseValuePropSlides(raw);
  if (shape.length === 0) {
    return [];
  }

  return Array.from({ length: shape.length }, (_, i) => {
    const headline = String(t(`${VALUE_PROP_SLIDES_KEY}.${i}.headline` as never));
    const description = String(t(`${VALUE_PROP_SLIDES_KEY}.${i}.description` as never));
    return { headline, description };
  });
}
